import React from 'react';
import { createPortal } from 'react-dom';

interface SuccessRescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  oldDate: string;
  oldTime: string;
  newDate: string;
  newTime: string;
  professionalName: string;
  serviceName: string;
}

const SuccessRescheduleModal: React.FC<SuccessRescheduleModalProps> = ({
  isOpen,
  onClose,
  oldDate,
  oldTime,
  newDate,
  newTime,
  professionalName,
  serviceName
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-[9999] md:p-4 animate-fadeIn">
        <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl transform animate-slideUp">
          {/* Header con animación de éxito */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center relative overflow-hidden">
            {/* Círculos decorativos de fondo */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full translate-x-20 translate-y-20"></div>
            
            {/* Icono de éxito con animación */}
            <div className="relative z-10">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 animate-checkmark">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">¡Cita Reagendada!</h2>
              <p className="text-green-100">Tu cita ha sido actualizada exitosamente</p>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-6">
            {/* Comparación de fechas */}
            <div className="space-y-4 mb-6">
              {/* Fecha anterior */}
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-red-900">Fecha Anterior</span>
                </div>
                <p className="text-sm text-red-800 ml-11 capitalize">{formatDate(oldDate)}</p>
                <p className="text-lg font-bold text-red-900 ml-11">{oldTime}</p>
              </div>

              {/* Flecha de cambio */}
              <div className="flex justify-center">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>

              {/* Nueva fecha */}
              <div className="bg-green-50 border-l-4 border-green-400 rounded-r-lg p-4">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-green-900">Nueva Fecha</span>
                </div>
                <p className="text-sm text-green-800 ml-11 capitalize">{formatDate(newDate)}</p>
                <p className="text-lg font-bold text-green-900 ml-11">{newTime}</p>
              </div>
            </div>

            


            {/* Botones de acción */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos para animaciones */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes checkmark {
          0% {
            transform: scale(0) rotate(-45deg);
          }
          50% {
            transform: scale(1.2) rotate(5deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-checkmark {
          animation: checkmark 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both;
        }
      `}</style>
    </>,
    document.body
  );
};

export default SuccessRescheduleModal;